import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import debounce from '@doveconviene/sf-web-js-sdk/debounce'

import styled from '@emotion/styled'

import { useSelector } from 'react-redux'
import useOnScreen from '../../../ui/hooks/useOnScreen'

import createStaticMapUrl from '../../../../helpers/createStaticMapUrl'

import { selectUserGeolocation } from '../../../../flux/selectors/user'
import { selectCountry } from '../../../../flux/selectors/ux'
import { getSecret } from '../../../../utils/i18n/secret'

const Wrapper = styled.div({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
})

const BlurredBackground = styled.div(({ src }) => ({
  position: 'absolute',
  background: '#EBE9E5',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundImage: src ? `url('${src}')` : undefined,
  filter: 'blur(25px)',
  zIndex: -1,
}))

const StyledMap = styled.img(({ src }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  opacity: src ? 1 : 0,
  transition: 'opacity .2s ease-in',
  width: '100%',
  height: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
  margin: '0 auto',
  objectFit: 'scale-down',
}))

const StaticMap = ({
  width,
  height,
  markers,
  containerRef,
}) => {
  const [src, setSrc] = React.useState(null)
  const [renderMap, setRenderMap] = React.useState(false)
  const country = useSelector(selectCountry)
  const gmapKey = getSecret('GOOGLE_MAPS_API_KEY')({ country })
  const userGelocation = useSelector(selectUserGeolocation)
  const wrapperRef = useRef()

  useOnScreen({
    target: wrapperRef,
    onEnterScreen: () => {
      setRenderMap(true)
    },
    disconnectOnLeave: true,
  }, { root: containerRef })

  const updateSrc = (debounce(async () => {
    const { current: wrapper } = wrapperRef
    if (wrapper && renderMap) {
      createStaticMapUrl({
        gmapKey,
        size: {
          width: width || wrapper.clientWidth,
          height: height || wrapper.clientHeight,
        },
        position: userGelocation,
        markers,
      }).then(url => {
        setSrc(url)
      })
    }
  }, 350))

  React.useLayoutEffect(() => {
    window.addEventListener('resize', updateSrc)
    updateSrc()
    return () => window.removeEventListener('resize', updateSrc)
  }, [])

  React.useEffect(() => {
    const { current: wrapper } = wrapperRef
    if (wrapper && renderMap) {
      updateSrc(wrapper)
    }
  }, [updateSrc, width, height, userGelocation, markers])

  return (
    <Wrapper ref={wrapperRef} data-test="StaticMap">
      <BlurredBackground src={src} />
      <StyledMap src={src} alt="" />
    </Wrapper>
  )
}

StaticMap.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  markers: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    lng: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    pin: PropTypes.string,
  })),
  containerRef: PropTypes.shape({}),
}

StaticMap.defaultProps = {
  width: null,
  height: null,
  markers: [],
  containerRef: null,
}

StaticMap.displayName = 'StaticMap'
StaticMap.whyDidYouRender = true

export default StaticMap
